import PropTypes from "prop-types";
import React, { createContext, useContext } from "react";

import { useAppSetting } from "@swa-ui/application";
import { usePersistedState } from "@swa-ui/persistence";
import { PlacementsContext, usePlacementIds } from "@swa-ui/placements";

import { navigationSystemMessagesAppSetting } from "../defines/navigationItems";
import { SiteStatusContext } from "../SiteStatusProvider";

export const SystemMessagesContext = createContext({
  addViewedMessageAdvisoryIds: () => {},
  notificationMessagePlacementIds: [],
  shouldShowSystemMessagesInNavigation: false,
  systemMessageAdvisoryIds: [],
  systemMessagePlacementIds: [],
  viewedMessageAdvisoryIds: [],
});

export const systemMessagePlacementIdPrefixes = [
  "messageIncidentAlert",
  "messageTravelAdvisory",
  "messageSystemAlert",
];

export const notificationMessagePlacementIdPrefixRE = /^notificationAlert(\d)*$/;

/**
 * SystemMessagesProvider provides information about which
 * system messages have been viewed.
 */
export const SystemMessagesProvider = (props) => {
  const { children } = props;
  const { placements } = useContext(PlacementsContext);
  const enableNavigationSystemMessages = useAppSetting(navigationSystemMessagesAppSetting);
  const { siteStatus: { fitState } = {} } = useContext(SiteStatusContext);
  const [viewedMessageAdvisoryIds, setViewedMessageAdvisoryIds] = usePersistedState({
    defaultValue: [],
    key: "SystemMessageProvider-viewedMessageAdvisoryIds",
  });
  const systemMessagePlacementIds = getSystemMessagePlacementIdsToRender(placements, fitState);
  const notificationMessagePlacementIds = usePlacementIds(notificationMessagePlacementIdPrefixRE);
  const notificationMessageAdvisoryIds = getSystemMessageAdvisoryIds();

  return (
    <SystemMessagesContext.Provider
      value={{
        addViewedMessageAdvisoryIds,
        notificationMessageAdvisoryIds,
        notificationMessagePlacementIds,
        shouldShowSystemMessagesInNavigation: enableNavigationSystemMessages,
        systemMessagePlacementIds,
        viewedMessageAdvisoryIds,
      }}
    >
      {children}
    </SystemMessagesContext.Provider>
  );

  function getSystemMessagePlacementIdsToRender(allPlacements, fit) {
    const allSystemMessagePlacementIds = systemMessagePlacementIdPrefixes.map((prefix) =>
      getPlacementId(prefix, fit)
    );

    return allSystemMessagePlacementIds.filter((systemMessagePlacementId) =>
      Object.keys(allPlacements).includes(systemMessagePlacementId)
    );
  }

  function getPlacementId(placementId, fit) {
    return fit ? `${placementId}fit` : placementId;
  }

  function getSystemMessageAdvisoryIds() {
    return notificationMessagePlacementIds.reduce((advisoryIds, notificationMessagePlacementId) => {
      const placement = placements?.[notificationMessagePlacementId]?.content;

      return advisoryIds.concat(
        placement?.messageList?.reduce(
          (acc, message) => acc.concat(message.messageText.map((text) => text.advisoryId)),
          []
        )
      );
    }, []);
  }

  function addViewedMessageAdvisoryIds(addedMessageIds) {
    const newViewedMessageIds = [...new Set([...viewedMessageAdvisoryIds, ...addedMessageIds])];

    setViewedMessageAdvisoryIds(newViewedMessageIds);
  }
};

SystemMessagesProvider.displayName = "SystemMessagesProvider";
SystemMessagesProvider.propTypes = {
  /** Content that will be rendered. */
  children: PropTypes.node.isRequired,
};
